import PropTypes from 'prop-types';

/**
 * Authoring UI for the `maxTotalDuration` top-level schema key.
 *
 * Production shape:
 *   {
 *     arrayField: string,
 *     timeInField: string,
 *     timeOutField: string,
 *     maxHours: number,
 *   }
 *
 * Business meaning: for an attendance list, calculate duration from each
 * Time In / Time Out pair and reject submissions when the total exceeds
 * the configured maximum, usually 24 hours.
 */
export default function MaxTotalDurationForm({
	value,
	onChange,
	disabled = false,
	fieldOptions = [],
}) {
	const safe = value && typeof value === 'object' ? value : {};
	const isAuthored = !!(safe.arrayField || safe.timeInField || safe.timeOutField || safe.maxHours != null);

	const update = (patch) => {
		const merged = { ...safe, ...patch };
		const nonEmpty = merged.arrayField || merged.timeInField || merged.timeOutField || merged.maxHours != null;
		onChange(nonEmpty ? merged : null);
	};

	const updateMaxHours = (text) => {
		if (text === '') {
			update({ maxHours: undefined });
			return;
		}
		const n = Number(text);
		update({ maxHours: Number.isNaN(n) ? text : n });
	};

	const clear = () => onChange(null);
	const fieldListId = 'cv-mtd-field-options';

	return (
		<div className="dsv-cv-form" data-testid="cv-mtd-form">
			<p className="dsv-cv-form__hint">
				Use this for attendance records. It adds up the time between each
				Time In and Time Out, then checks the total does not exceed the
				allowed hours.
			</p>

			<datalist id={fieldListId}>
				{fieldOptions.map((opt) => <option key={opt} value={opt} />)}
			</datalist>

			<div className="dsv-modal__field-row">
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label">Attendance List Field</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.arrayField ?? ''}
						disabled={disabled}
						onChange={(e) => update({ arrayField: e.target.value })}
						placeholder="e.g. person_attendance_details"
					/>
				</div>
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label">Maximum Total Hours</label>
					<input
						className="dsv-modal__input"
						type="number"
						value={safe.maxHours ?? ''}
						disabled={disabled}
						onChange={(e) => updateMaxHours(e.target.value)}
						placeholder="e.g. 24"
					/>
				</div>
			</div>

			<div className="dsv-modal__field-row">
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label">Time In Field</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.timeInField ?? ''}
						disabled={disabled}
						onChange={(e) => update({ timeInField: e.target.value })}
						placeholder="e.g. time_in"
					/>
				</div>
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label">Time Out Field</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.timeOutField ?? ''}
						disabled={disabled}
						onChange={(e) => update({ timeOutField: e.target.value })}
						placeholder="e.g. time_out"
					/>
				</div>
			</div>

			{!disabled && isAuthored && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary"
					onClick={clear}
					data-testid="cv-mtd-clear"
				>
					Clear
				</button>
			)}
		</div>
	);
}

MaxTotalDurationForm.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	fieldOptions: PropTypes.arrayOf(PropTypes.string),
};
